import { cn } from "@/lib/utils";
import type { ItemQuality } from "@/lib/types";

interface ItemIconProps {
  /** Direct CDN URL (Blizzard render.worldofwarcraft.com) — preferred. */
  iconUrl?: string;
  /** Local icon basename — used for mock data icons under /public/icons. */
  iconName?: string;
  size?: number;
  quality?: ItemQuality;
  className?: string;
  alt?: string;
}

/**
 * Item icon with three resolution paths:
 *   1. iconUrl  → direct from Blizzard's media API
 *   2. iconName → local PNG converted from Interface\ICONS at build time
 *   3. neither  → questionmark fallback
 */
export function ItemIcon({
  iconUrl,
  iconName,
  size = 36,
  quality,
  className,
  alt = "",
}: ItemIconProps) {
  const url = iconUrl
    || (iconName ? `/icons/${iconName.toLowerCase()}.png` : null)
    || `/icons/inv_misc_questionmark.png`;
  const borderColor = quality
    ? `var(--color-quality-${quality})`
    : "var(--color-border-strong)";

  return (
    <span
      className={cn(
        "inline-block shrink-0 overflow-hidden rounded-xs bg-surface-2",
        className,
      )}
      style={{
        width: size,
        height: size,
        outline: `1px solid ${borderColor}`,
        outlineOffset: "-1px",
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt={alt}
        width={size}
        height={size}
        className="block h-full w-full"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </span>
  );
}
